import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  MessageCircle,
  User,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChatHistory = ({ user }) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/history`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error("Failed to load chat history:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return "Yesterday";
    } else if (days < 7) {
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-600 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case "cancelled":
        return <Badge variant="destructive" className="text-xs"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      case "pending":
        return <Badge variant="secondary" className="text-xs"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "accepted":
        return <Badge className="bg-blue-600 text-xs"><User className="w-3 h-3 mr-1" />Active</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">{status}</Badge>;
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  const getOtherParticipant = (conversation) => {
    if (user?.role === "veteran") {
      return {
        name: conversation.responder_name,
        role: "Responder"
      };
    }
    return {
      name: conversation.veteran_name,
      role: "Veteran"
    };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
            <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
              Conversations
            </span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Card className="card-valor">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <MessageCircle className="w-5 h-5 text-primary" />
              Chat History
            </CardTitle>
            <CardDescription>
              View your past conversations with {user?.role === "veteran" ? "responders" : "veterans"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {conversations.length === 0 ? (
              <div className="text-center py-12">
                <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No conversations yet</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Your chat history will appear here after you connect with someone
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {conversations.map((conversation) => {
                  const other = getOtherParticipant(conversation);
                  return (
                    <div
                      key={conversation.request_id}
                      onClick={() => navigate(`/chat/${conversation.request_id}`)}
                      className="flex items-center gap-4 p-4 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors border border-transparent hover:border-border"
                      data-testid={`conversation-${conversation.request_id}`}
                    >
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-primary text-white">
                          {getInitials(other.name)}
                        </AvatarFallback>
                      </Avatar>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-foreground truncate">{other.name || "Unknown"}</p>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(conversation.last_message?.created_at)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(conversation.status)}
                          <span className="text-xs text-muted-foreground">
                            {conversation.message_count} message{conversation.message_count !== 1 ? "s" : ""}
                          </span>
                        </div>
                        {conversation.last_message && (
                          <p className="text-sm text-muted-foreground truncate mt-1">
                            {conversation.last_message.sender_role === (user?.role === "veteran" ? "veteran" : "responder") 
                              ? "You: " 
                              : ""}
                            {conversation.last_message.message}
                          </p>
                        )}
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ChatHistory;
